import * as React from "react";
import {Link, useNavigate, useParams} from "react-router";
import {Categories} from "../../data/numericorder/numericorder";
import useSqids from "../../hooks/useSqids";
import useSeededRandom from "../../hooks/useSeededRandom";
import shareLink from "../../helpers/shareLink";

export default function Numericorder() {

    const sqids = useSqids();

    const seededRandom = useSeededRandom();

    const params = useParams();

    const navigate = useNavigate();

    const audioRef = React.useRef(null);

    React.useEffect(() => {
        audioRef.current = new Audio('/632663__xkeril__task-successful-sfx.wav');
        audioRef.current.preload = "auto";
    }, []);

    const [guess, setGuess] = React.useState('');

    const [guesses, setGuesses] = React.useState([]);

    const [incorrectGuesses, setIncorrectGuesses] = React.useState([]);

    const category = React.useMemo(() => {
        const [id] = sqids.decode(params.category);
        return Categories.at(id);
    }, [params, sqids]);

    const wordIndex = React.useMemo(() => {
        const rng = seededRandom(params.date);
        return Math.floor(rng() * category.entries.length);
    }, [params, category, seededRandom]);

    const success = guesses.indexOf(wordIndex) > -1;

    React.useEffect(() => {
        if(success){
            audioRef.current.play();
        }
    }, [success]);

    const normalizeDate = (date) => {
        return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
        );
    }

    const dateFromDaysSince1980 = (days) => {
        const start = normalizeDate(new Date(days * 24 * 60 * 60 * 1000));
        return start;
    }

    const selectedDate = React.useMemo(() => {
        return dateFromDaysSince1980(params.date);
    }, [params]);

    React.useEffect(() => {
        if (!category) {
            navigate(`/numericorder/`);
        } else if (selectedDate > normalizeDate(new Date())) {
            navigate(`/numericorder/${params.category}`);
        }
    }, [selectedDate, category])

    const makeGuess = () => {
        const cleanGuess = guess.toLowerCase().trim();
        const idx = category.entries.findIndex(g => g.name.toLowerCase() === cleanGuess);
        if (idx > -1 && guesses.indexOf(idx) < 0) {
            setGuesses((g) => g.concat(idx).sort((b, a) => b - a));
        } else if (idx < 0 && incorrectGuesses.indexOf(cleanGuess) < 0) {
            setIncorrectGuesses(g => g.concat(cleanGuess).sort((b, a) => b.localeCompare(a)));
        }
        setGuess('');
    };

    const hints = React.useMemo(() => {
        return category.entries
            .filter((e, i) => !guesses.includes(i))
            .map(e => e.name)
            .sort((a, b) => a.localeCompare(b))
    }, [category, guesses]);

    console.log(category.entries[wordIndex].name);

    return <div style={{maxWidth: "500px"}}>
        <h1 className="text-center display-1 mb-2 mb-md-4">
            <Link className="text-dark" to={"/"}>Daily Puzzles</Link>
        </h1>

        <h2>
            <Link className="text-dark" to={"/numericorder"}>Numericorder</Link>
        </h2>

        <p className="lead">
            Guess the word based on it's numeric ranking in the list.
        </p>

        <h3 className="text-center">
            {category.name}&nbsp;
            <span
                className="badge rounded-pill text-bg-secondary">{category.entries.length}</span>
            <div className="small text-secondary">{category.description}</div>
        </h3>

        <h5 className="text-center">
            {selectedDate.toDateString()}
        </h5>

        <form onSubmit={(e) => {
            e.preventDefault();
            makeGuess()
        }} className="input-group">
            <input type="search" className="form-control" value={guess} disabled={success}
                   placeholder="Enter your guess." list="suggestions"
                   onChange={(e) => setGuess(e.target.value)}/>

            <datalist id="suggestions">
                {hints.map(c => <option key={c} value={c}></option>)}
            </datalist>

            <button className="btn btn-outline-dark" type="submit" disabled={guess.length === 0 || success}
                    title="Search"><i
                className="bi bi-search"></i>
            </button>
        </form>

        {incorrectGuesses.length > 0 ? (
            <p className="mt-2">
                Not in list: {incorrectGuesses.map(i => <span className="badge bg-danger me-1" key={i}>{i}</span>)}
            </p>
        ) : <></>}

        {success ?
            <div className="alert alert-success mt-2">
                Well done! You took {guesses.length} guess{guesses.length > 1 ? "es" : ""}.&nbsp;
                <a href="#" className="text-success" onClick={() => shareLink({
                    title: `${category.name} - ${selectedDate.toDateString()}`,
                    text: `I got it in ${guesses.length}. Try to beat me!`,
                    url: window.location
                })}>Want to brag about it?</a>
            </div>
            : <></>}

        <div className="list-group mt-2">
            {guesses.filter(g => g < wordIndex).map((g, i) =>
                <div key={i} className="list-group-item d-flex w-100 justify-content-between">
                    {category.entries[g].name} ({category.entries[g].displayValue})
                    <span className="badge bg-info"><i className="bi bi-caret-down-fill"></i>{wordIndex - g}</span>
                </div>)}
            {guesses.length > 0 ?
                (success ?
                        <div className="list-group-item list-group-item-success">
                            {category.entries[wordIndex].name} ({category.entries[wordIndex].displayValue})
                        </div> :
                        <div className="list-group-item">
                            <span className="badge bg-success">???</span>
                        </div>
                )
                : <></>}
            {guesses.filter(g => g > wordIndex).map((g, i) =>
                <div key={i} className="list-group-item d-flex w-100 justify-content-between">
                    {category.entries[g].name} ({category.entries[g].displayValue})
                    <span className="badge bg-info"><i className="bi bi-caret-up-fill"></i>{g - wordIndex}</span>
                </div>)}
        </div>

    </div>
}
